// Shared pharmacy-label parser — used by the consult dashboard's Paste
// button and the Locker client form. Single source of truth.

export const parsePatientPaste = (raw) => {
  const out = {};
  if (!raw || typeof raw !== "string") return out;
  const cleaned = raw
    .replace(/\r/g, "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "");   // strip zero-width chars
  const lines = cleaned.split("\n").map((l) => l.trim()).filter(Boolean);

  // 1) HCN — search the whole text for `HCN|PHN|Health Card [Number]: <10 digits>` and
  //    OPTIONALLY 2 uppercase-letter version code AFTER the digits.
  //    The 2-letter code is only accepted when it stands alone (i.e. NOT
  //    followed by another letter), otherwise the parser was greedily
  //    swallowing the first two letters of adjacent labels like "Sex" or
  //    a "Last, First" name that immediately followed the digits.
  const hcnMatch = cleaned.match(/\b(?:HCN|PHN|Health\s*Card(?:\s*Number)?)\s*:\s*(\d{10})(?:\s*([A-Za-z]{2})(?![A-Za-z]))?/i);
  if (hcnMatch) out.hcn = `${hcnMatch[1]}${(hcnMatch[2] || "").toUpperCase()}`;

  // 2) DOB — first look for a labelled line, else fall back to any
  //    bare YYYY-MM-DD / MMM D, YYYY that isn't the current timestamp.
  const dobLabel = cleaned.match(/\b(?:DOB|Date\s*of\s*birth|Birth(?:day)?)\s*:\s*([^\n]+)/i);
  const tryDate = (v) => {
    const s = v.trim();
    // Pure YYYY-MM-DD (or YYYY/MM/DD) — parse as LOCAL date. If we hand
    // `new Date("1979-08-26")` to JS it treats the string as UTC midnight,
    // which shifts to the previous day in every timezone west of UTC.
    const isoMatch = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
    if (isoMatch) {
      const [, y, m, d] = isoMatch;
      const yy = Number(y), mm = Number(m), dd = Number(d);
      if (yy < 1900 || yy > 2100 || mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
      return `${y}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
    }
    // Other formats (e.g. "Aug 26, 1979") — JS parses those as local time
    // so getFullYear/getMonth/getDate return the intended day.
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    if (y < 1900 || y > 2100) return null;
    return `${y}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  if (dobLabel) {
    const parsed = tryDate(dobLabel[1].replace(/\s+(Sex|Gender|Address|Phone).*/i, ""));
    if (parsed) out.dob = parsed;
  }

  // 3) Name — try labelled form first ("RE: / Name: / Patient name:"),
  //    else look for a "Last, First" line (letters + comma + letters).
  const nameLabel = cleaned.match(/\b(?:RE|Name|Patient(?:\s*name)?)\s*:\s*([^\n]+)/i);
  if (nameLabel) {
    out.patient_name = nameLabel[1].trim();
  } else {
    for (const line of lines) {
      // "Last, First"  — must be plain-letter tokens on both sides of a
      // comma. Ignore address/city lines by rejecting anything with
      // digits or common address tokens on it.
      if (/\d/.test(line)) continue;
      if (/\b(street|st\.?|ave\.?|blvd\.?|road|rd\.?|drive|dr\.?|apt|unit|ontario|quebec|ON|QC)\b/i.test(line)) continue;
      const m = line.match(/^([A-Za-zÀ-ÿ'\- ]+),\s*([A-Za-zÀ-ÿ'\- ]+)$/);
      if (m) {
        out.patient_name = `${m[2].trim()} ${m[1].trim()}`;   // "First Last"
        break;
      }
    }
  }

  return out;
};

// Split parsePatientPaste's name into parts. Handles both "First Last"
// and labelled "Last, First" forms from pharmacy labels.
export const splitName = (full) => {
  let s = (full || "").trim();
  if (!s) return { first_name: "", last_name: "" };
  if (s.includes(",")) {
    const [last, first] = s.split(",", 2);
    return { first_name: (first || "").trim(), last_name: (last || "").trim() };
  }
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { first_name: parts[0], last_name: "" };
  return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
};
